import { User } from 'fitify-types/src/types/user'
import type { VisualGoalComparisonProps } from 'fitify-ui-onboarding/src/components/Paywall/VisualGoalComparison/VisualGoalComparison'
import { useTranslation } from 'next-i18next/pages'
import { useCallback, useMemo } from 'react'

import { useCategoryImageVariant } from 'hooks/useImageProps'
import {
  BodyFat,
  FitnessLevel,
  getGoalBodyFat,
  getGoalImage,
  getNowBodyFat,
  getNowFitnessLevel,
  getNowImage,
  type GoalComparisonFunnel,
} from 'sections/subscription/GoalComparison/GoalComparison.Utils'
import { useFunnelState } from 'utils/state'

type Props = {
  funnel: GoalComparisonFunnel
}

export const useGoalComparison = ({
  funnel,
}: Props): VisualGoalComparisonProps => {
  const { t } = useTranslation()
  const { data } = useFunnelState(funnel)
  const imageVariant = useCategoryImageVariant(funnel)

  const nowBodyFat = useMemo(() => {
    return getNowBodyFat({
      current_shape: data.current_shape,
    })
  }, [data.current_shape])

  const goalBodyFat = useMemo(
    () =>
      getGoalBodyFat({
        gender: data.gender,
        bodyGoal: data.body_goal,
      }),
    [data.gender, data.body_goal]
  )

  const nowFitnessLevel = useMemo(() => {
    return getNowFitnessLevel({
      how_often_exercise: data.how_often_exercise,
    })
  }, [data.how_often_exercise])

  const fitnessLevelTitle = useMemo(() => {
    switch (data.goal) {
      case User.Profile.Goals.GainMuscle:
      case User.Profile.Goals.GetToned:
        return t('paywall_muscle_mass')
      case User.Profile.Goals.GetFit:
      case User.Profile.Goals.LoseWeight:
      default:
        return t('paywall_fitness_level')
    }
  }, [data.goal, t])

  const getBodyFatLabel = useCallback(
    (bodyFat: BodyFat) => {
      switch (bodyFat) {
        case BodyFat.Low:
          return t('paywall_body_fat_low')
        case BodyFat.Medium:
          return t('paywall_body_fat_normal')
        case BodyFat.High:
          return t('paywall_body_fat_high')
        case BodyFat.VeryHigh:
        default:
          return t('paywall_body_fat_very_high')
      }
    },
    [t]
  )

  const getFitnessLevelLabel = useCallback(
    (fitnessLevel: FitnessLevel) => {
      if (
        data.goal &&
        [User.Profile.Goals.GainMuscle, User.Profile.Goals.GetToned].includes(
          data.goal
        )
      ) {
        switch (fitnessLevel) {
          case FitnessLevel.Medium:
            return t('paywall_muscle_mass_medium')
          case FitnessLevel.High:
            return t('paywall_muscle_mass_high')
          case FitnessLevel.Low:
          default:
            return t('paywall_muscle_mass_low')
        }
      }
      switch (fitnessLevel) {
        case FitnessLevel.Medium:
          return t('paywall_intermediate')
        case FitnessLevel.High:
          return t('paywall_advanced')
        case FitnessLevel.Low:
        default:
          return t('paywall_beginner')
      }
    },
    [data.goal, t]
  )

  return useMemo(() => {
    const goalFitnessLevel = FitnessLevel.High

    return {
      fitnessLevelTitle,
      goal: {
        bodyFat: getBodyFatLabel(goalBodyFat),
        fitnessLevel: getFitnessLevelLabel(goalFitnessLevel),
        fitnessLevelProgress: goalFitnessLevel,
        image: getGoalImage({
          funnel,
          bodyGoal: data.body_goal,
          variant: imageVariant,
        }),
      },
      now: {
        bodyFat: getBodyFatLabel(nowBodyFat),
        fitnessLevel: getFitnessLevelLabel(nowFitnessLevel),
        fitnessLevelProgress: nowFitnessLevel,
        image: getNowImage({ funnel, nowBodyFat, variant: imageVariant }),
      },
    }
  }, [
    data.body_goal,
    fitnessLevelTitle,
    funnel,
    getBodyFatLabel,
    goalBodyFat,
    getFitnessLevelLabel,
    imageVariant,
    nowBodyFat,
    nowFitnessLevel,
  ])
}
